import React from 'react';
import PropTypes from 'prop-types';

import proNotifications from '../../../modules/proNotifications';
import Janitor from '../../../modules/Janitor';
import welcomeNotificationModule from '../../../modules/welcomeNotificationModule';
import SSErrorBoundary from '../../SSErrorBoundary';
import ssPropTypes from '../../../modules/ssPropTypes';
import { onReceiveNotifications as onReceiveVirtualServicesNotification } from './virtualServices/showVirtualServicesModal';
import { onReceiveNotifications as onReceiveProductSalesNotification } from './productSales/showProductSalesModal';
import { ModalContextPropType } from '../../ui/ModalContext';
import { getComponent } from './NotificationFeedComponentMap';

import './NotificationFeed.scss';

/**
 * Displays the list of notifications for a provider.
 *
 * @example
 *   <NotificationFeed providerId={32} />
 *
 * In order to mark the feed as viewed, pass the viewing prop
 * @example
 *   <NotificationFeed providerId={32} viewing={true} />
 */
export default class NotificationFeed extends React.PureComponent {
  static propTypes = {
    /** The provider id */
    providerId: ssPropTypes.numberLike.isRequired,

    /** If the notification is currently being viewed.
        (i.e. it could be loaded, but hidden)
        If `true`, all the notifications visible will be marked as viewed */
    viewing: PropTypes.bool,

    /** A callback function which will get the notifications list when loaded. */
    onNotificationsLoaded: PropTypes.func,

    /** Modal context which allows adding and removing of modals from user view */
    modalContext: ModalContextPropType,
  };

  static defaultProps = {
    viewing: false,
    onNotificationsLoaded: null,
    modalContext: null,
  };

  state = {
    notifications: [],
    isLoading: true,
  };

  janitor = new Janitor();

  componentDidMount() {
    // Kick-start the notifications stream.  Only track notifications sent from this
    // instance.
    proNotifications.getAll(this.props.providerId, { trackNotifications: true })
      .then(() => {
        // Wait until notifications list is populated before subscribing to avoid calling the
        // handler multiple times in succession
        this.janitor.addStream(proNotifications.notificationsStream)
          .subscribe(this.onReceiveNotifications);
      });
  }

  componentDidUpdate() {
    this.markAsSeen(this.state.notifications);
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  onReceiveNotifications = notifications => {
    if (!notifications) return;
    onReceiveVirtualServicesNotification(notifications, this.props.modalContext);
    onReceiveProductSalesNotification(notifications, this.props.modalContext);

    this.markAsSeen(notifications);
    this.setState({
      notifications,
      isLoading: false,
    });

    // Fire callback function
    if (typeof this.props.onNotificationsLoaded === 'function') {
      try {
        this.props.onNotificationsLoaded(notifications);
      } catch (e) {
        console.error(e);
      }
    }
  };

  /**
   * Mark the notifications as viewed if we're currently viewing the list
   * @param {List} notifications - The notifications list
   */
  markAsSeen(notifications) {
    if (this.props.viewing === true && notifications.length) {
      // remember we saw these transient notifications
      proNotifications.setLastNotficationSeen(notifications);
      // also remember we saw each of the persistent notifications.
      proNotifications.setPersistentNotificationsSeen(notifications);
      welcomeNotificationModule.setWelcomeNotificationSeenState();
    }
  }

  /**
   * Returns a react element for a notification item.
   * @param {Object} notification  Notification object
   * @returns {Element}
   */
  getComponentElement(notification) {
    const componentConfig = getComponent(notification.type);
    if (componentConfig) {
      const { type } = componentConfig;
      let { props } = componentConfig;
      if (typeof props === 'function') {
        // some notifications have a props function for dynamic props, some have a
        // static props object
        props = props(notification);
      }
      const component = React.createElement(
        type,
        {
          ...props,
          notification,
          key: notification.creation_time,
          modalContext: this.props.modalContext,
          providerId: this.props.providerId,
        },
      );
      return component;
    }
    return null;
  }

  render() {
    const { notifications, isLoading } = this.state;

    return (
      <section className="notification-feed">
        {isLoading
          ? <div className="notification-feed-loading" />
          : (
            <ol data-testid="notificationFeedList">
              {notifications.map(notification => (
                <SSErrorBoundary key={notification.uuid}>
                  <li>
                    {this.getComponentElement(notification)}
                  </li>
                </SSErrorBoundary>
              ))}
            </ol>
          )}
      </section>
    );
  }
}
